import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";

/**
 * Minimal slide-in drawer: click-backdrop / Escape / close-button to dismiss,
 * scroll-locked while open. Modeled on {@link Modal} and kept dependency-free
 * (no Radix) to stay within the SPA/artifact CSP + bundle budget. Slides in from
 * the left by default (pass `side="right"` for a right-hand drawer); the
 * entrance transition honours prefers-reduced-motion.
 */
export function Sheet({
  title,
  onClose,
  children,
  className,
  side = "left",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  side?: "left" | "right";
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label={title}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "absolute inset-y-0 z-10 flex w-72 max-w-[80vw] flex-col overflow-hidden bg-background shadow-xl",
          "motion-safe:animate-in motion-safe:duration-200",
          side === "right"
            ? "right-0 motion-safe:slide-in-from-right"
            : "left-0 motion-safe:slide-in-from-left",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b border-border p-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X aria-hidden />
            <span className="sr-only">{title}</span>
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
